import type {
  Notification as NotificationData,
  NotificationCta,
  NotificationPayload,
  NotificationPayloadType,
} from '@mathflat/domain/Notification'
import { isThisYear, isToday } from 'date-fns'
import { action, computed, makeObservable, observable } from 'mobx'

import { notificationController } from '@/controller/notification.controller'
import { localStorageService } from '@/service/storage.service'
import dateUtils from '@/utils/DateUtils'

import { DTO } from '../DTO'

export type NotificationCTA =
  | 'NONE'
  | 'READ'
  | 'FILE_DOWNLOAD'
  | 'FILE_DOWNLOAD_API'
  | 'REDIRECT'
  | 'FILE_DOWNLOAD_ID_LIST'
  | 'PREVIEW'
  | 'CLIENT_NOTIFICATION_PUBLIC_WORKBOOK_OPEN'
  | 'CLIENT_NOTIFICATION_ESSAY_WORKSHEET_OPEN'
export type NotificationCTAOption = {
  onCTAClick?: (e: React.MouseEvent) => void
  text?: string | null
  className?: string
}
export type NotificationServeType = 'SERVER' | 'CLIENT' // 메세징 서버에서 받아오는 알람 | FE(CLIENT)에서 생성하는 알림 구분
export class Notification extends DTO {
  readonly serveType: NotificationServeType
  sortKey: number
  isRead: boolean
  message: string
  title: null | string
  createdAt: Date
  expiredAt: Date
  type: NotificationPayloadType
  id: string
  case2: NotificationPayload['case']
  state: NotificationPayload['state']
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  ctas: NotificationCta[]

  constructor(data: NotificationData, serveType: NotificationServeType = 'SERVER') {
    super()
    makeObservable(this, {
      sortKey: observable,
      isRead: observable,
      message: observable,
      title: observable,
      createdAt: observable,
      expiredAt: observable,
      type: observable,
      id: observable,
      case2: observable,
      state: observable,
      payload: observable,
      isTodayNotification: computed,
      formatToNotificationDatetime: computed,
      read: action,
      readAndFetch: action,
      ctas: observable,
      serveType: observable,
    })

    const payload = data.data?.payload
    this.isRead = data.read
    // 만료 시각이 없으면 지난 것으로 본다(서버는 저장할 때 늘 채운다)
    this.expiredAt = new Date(data.expireDateTime ?? 0)
    this.createdAt = new Date(data.createDateTime)
    this.message = payload?.message ?? ''
    this.title = payload?.title ?? null
    this.sortKey = data.sortKey
    this.type = payload?.type ?? 'DEFAULT'
    // id 는 payload 종류마다 실리는 키라 스키마가 선언하지 않는다. 서버 payload 클래스는 문자열로 낸다
    this.id = payload?.id == null ? '' : String(payload.id)
    this.case2 = payload?.case
    this.state = payload?.state
    this.payload = payload ?? {}
    this.ctas = data.data?.cta ?? []
    this.serveType = serveType
  }

  get isTodayNotification() {
    return isToday(this.createdAt)
  }
  get formatToNotificationDatetime() {
    if (this.isTodayNotification) return dateUtils.to오늘HourMinute(this.createdAt)
    if (isThisYear(this.createdAt)) return dateUtils.toKoMonthDay(this.createdAt)
    return dateUtils.toKoFullDate(this.createdAt)
  }

  read = () => {
    if (!this.isRead) {
      this.isRead = true
    }
  }
  readAndFetch = () => {
    if (!this.isRead) {
      const partitionKey = this.partitionKey

      this.isRead = true
      notificationController.readItem({
        partitionKey,
        params: { sortKey: this.sortKey },
      })
    }
  }

  private get partitionKey() {
    const academyId = localStorageService.getItem('ACADEMY_ID')!
    const teacherId = localStorageService.getItem('teacher-id')!
    return `${academyId}.${teacherId}`
  }
}
